/** Canlı destek widget'ının yüklenme koşulu ve oturum bilgisi.
 *
 * Widget'ı yalnızca ``LiveChatSupport`` çizer; sayfa içindeki kısayollar
 * (ör. canlı destekten talep edilen bonus kartı) durumu ``liveChatState`` ile
 * sorup pencereyi ``openLiveChat`` ile açar — böylece "widget var mı" kuralı
 * tek yerde kalır.
 */

const ORGANIZATION_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Widget'ın kimliği. LiveChat sayısal lisans, Text.com UUID biçimli
 * organizasyon kimliği ister; ikisi aynı widget'ı (`tracking.js`) yükler ve
 * aynı anda yalnızca biri seçilidir (bkz. backend/services/livechat_provider.py). */
const identityOf = (settings = {}) => {
  if (!settings.livechat_enabled) return null;
  if (settings.livechat_provider === 'text') {
    const organizationId = String(settings.livechat_organization_id || '')
      .trim().toLowerCase();
    return ORGANIZATION_RE.test(organizationId) ? { organizationId } : null;
  }
  const license = String(settings.livechat_license_id || '').trim();
  return /^\d{8,20}$/.test(license) ? { license } : null;
};

/** 'off' = widget hiç yüklenmez, 'login_required' = üye girişi bekleniyor. */
export const liveChatState = (settings = {}, user = null) => {
  if (!identityOf(settings)) return 'off';
  if (settings.livechat_require_login && !user) return 'login_required';
  return 'ready';
};

export const buildLiveChatConfig = (settings = {}, user = null) => {
  const identity = identityOf(settings);
  if (liveChatState(settings, user) !== 'ready') return null;

  const group = String(settings.livechat_group_id || '').trim();
  const fullName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
    : '';
  const customerName = fullName || user?.username || undefined;

  return {
    license: identity.license,
    organizationId: identity.organizationId,
    group: group || undefined,
    customerName,
    customerEmail: user?.email || undefined,
    sessionVariables: user ? {
      user_id: String(user.id || ''),
      username: String(user.username || ''),
    } : {
      visitor_type: 'guest',
    },
  };
};

/** Canlı destek penceresini açar; widget yüklü değilse false döner. */
export const openLiveChat = () => {
  const widget = typeof window !== 'undefined' ? window.LiveChatWidget : null;
  if (typeof widget?.call !== 'function') return false;
  // `maximize` gizlenmiş widget'ta çalışmaz (LiveChat sözleşmesi). Bastırma
  // etkinken — hazır spor kitabı açıkken — kabuklardaki "Canlı Destek"
  // düğmeleri bu yüzden sessizce hiçbir şey yapmıyordu: pencere önce görünür
  // kılınır, sonra büyütülür.
  let visibility = '';
  try {
    visibility = widget.get?.('state')?.visibility || '';
  } catch {
    visibility = '';
  }
  if (visibility === 'hidden') widget.call('minimize');
  widget.call('maximize');
  return true;
};

/* ---------- Bastırma ----------
 *
 * Tam ekran sağlayıcı çerçeveleri (oyun modalı, hazır spor kitabı) ekranın sağ
 * alt köşesini kendi düğmeleri için kullanır: Betby kuponu ve "Bahis yap"
 * düğmesi tam oraya düşer, 92px'lik canlı destek düğmesi ise onların üstünü
 * örtüyor ve oyuncu kuponu göremiyordu.
 *
 * Sayaçlıdır: iç içe açılan iki çerçeveden biri kapandığında widget geri
 * gelmemelidir. Durum modül belleğindedir — widget'ı yalnızca
 * ``LiveChatSupport`` çizer ve bastırmayı isteyen taraf onu bulamaz. */
let suppressCount = 0;
const suppressListeners = new Set();

const notifySuppression = () => {
  const suppressed = suppressCount > 0;
  suppressListeners.forEach((listener) => {
    try {
      listener(suppressed);
    } catch {
      // Bir dinleyicinin hatası diğerlerini durdurmamalı.
    }
  });
};

export const isLiveChatSuppressed = () => suppressCount > 0;

/** Widget'ı gizler; dönen fonksiyon (efekt temizliği) onu geri açar. */
export const suppressLiveChat = () => {
  suppressCount += 1;
  if (suppressCount === 1) notifySuppression();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    suppressCount = Math.max(0, suppressCount - 1);
    if (suppressCount === 0) notifySuppression();
  };
};

/** Bastırma değişimini dinler; dönen fonksiyon aboneliği bırakır. */
export const subscribeLiveChatSuppression = (listener) => {
  suppressListeners.add(listener);
  return () => { suppressListeners.delete(listener); };
};
